/**
 * RouteTransition — page/route transition wrapper (design system, FOUNDATION).
 *
 * Fade + rise on screen changes. The shell wraps its <Outlet/> with this
 * (keyed by pathname) so every tab/route swap animates; screen agents can
 * also wrap sub-views (wizards, steppers) by passing their own
 * `transitionKey`. Under prefers-reduced-motion the swap is instant (PRD §10).
 *
 * Usage (shell):     <RouteTransition transitionKey={location.pathname}>…
 * Usage (screens):   <RouteTransition transitionKey={step}>…
 */
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { pageVariants, usePrefersReducedMotion } from "./motion.js";

export interface RouteTransitionProps {
  /** Key that identifies the current screen; defaults to the route pathname. */
  transitionKey?: string;
  className?: string;
  children?: ReactNode;
}

export function RouteTransition({ transitionKey, className, children }: RouteTransitionProps) {
  const location = useLocation();
  const reduced = usePrefersReducedMotion();
  const key = transitionKey ?? location.pathname;
  if (reduced) {
    // instant swap — no exit hold, no rise
    return (
      <div className={className} key={key}>
        {children}
      </div>
    );
  }
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={key}
        className={className}
        variants={pageVariants}
        initial="initial"
        animate="enter"
        exit="exit"
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}
